import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { Listbox } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';

/**
 * IANA timezone selection, showing each zone's current time.
 *
 * The current time is the disambiguator. "Africa/Accra" means nothing to most people;
 * "Africa/Accra — 14:32" is instantly checkable against the clock on the wall.
 */

export interface TimezoneSelectProps {
  value: string | null;
  onChange: (timezone: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  'aria-describedby'?: string;
}

export const TimezoneSelect = ({
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled,
  invalid,
  id,
  popoverPortal = 'body',
  className,
  ...aria
}: TimezoneSelectProps) => {
  const options = useMemo(() => {
    const now = DateTime.now();

    return supportedTimezones().map((zone) => {
      const local = now.setZone(zone);
      const [region = '', ...rest] = zone.split('/');
      return {
        value: zone,
        label: rest.join('/').replace(/_/g, ' ') || zone,
        description: `${local.toFormat('HH:mm')} · ${local.offsetNameShort ?? ''}`,
        group: region.replace(/_/g, ' '),
        keywords: [zone, local.offsetNameShort ?? ''],
      };
    });
  }, []);

  return (
    <Listbox
      value={value}
      onChange={onChange}
      options={options}
      searchable
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      disabled={disabled}
      invalid={invalid}
      id={id}
      popoverPortal={popoverPortal}
      className={className}
      {...aria}
    />
  );
};

const supportedTimezones = (): string[] => {
  const supported = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  const zones = supported.supportedValuesOf?.('timeZone');
  if (zones && zones.length > 0) return zones;

  // Older engines: the church's own zone plus UTC is enough to keep a settings form
  // usable rather than empty.
  return [Intl.DateTimeFormat().resolvedOptions().timeZone, 'UTC'].filter(Boolean);
};
